import { useState, useEffect } from 'react'
import { 
  WhatsAppInstance, 
  MessageTemplate, 
  MessagingSettings, 
  MessageLog,
  MessageCampaign,
  ApiResponse 
} from '../types'
import { getConfig } from '../config/constants'

const config = getConfig()
const API_BASE = `${config.API_URL}/messaging`

export function useMessaging() {
  const [instances, setInstances] = useState<WhatsAppInstance[]>([])
  const [templates, setTemplates] = useState<MessageTemplate[]>([])
  const [settings, setSettings] = useState<MessagingSettings | null>(null)
  const [logs, setLogs] = useState<MessageLog[]>([])
  const [campaigns, setCampaigns] = useState<MessageCampaign[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Função para fazer requisições à API
  const apiRequest = async <T>(
    endpoint: string, 
    method: 'GET' | 'POST' | 'PUT' | 'DELETE' = 'GET', 
    data?: any
  ): Promise<ApiResponse<T>> => {
    try {
      const token = localStorage.getItem('admin_token')
      if (!token) {
        throw new Error('Token não encontrado')
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: data ? JSON.stringify(data) : undefined
      })

      const result = await response.json()

      if (!response.ok) {
        throw new Error(result.error || 'Erro na requisição')
      }

      return result
    } catch (error) {
      console.error('Erro na API:', error)
      throw error
    }
  }

  // Instâncias WhatsApp
  const fetchInstances = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<WhatsAppInstance[]>('/instances')
      setInstances(response.data || [])
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao buscar instâncias')
    } finally {
      setLoading(false)
    }
  }

  const createInstance = async (data: { 
    name: string; 
    phone: string;
    webhookUrl?: string; 
    webhookEnabled: boolean;
    ownerType?: string;
    ownerId?: string;
  }) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<WhatsAppInstance>('/instances', 'POST', data)
      setInstances(prev => [response.data!, ...prev])
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao criar instância')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const getInstanceQR = async (instanceId: string) => {
    try {
      console.log('🔍 Fazendo requisição para QR Code da instância:', instanceId)
      const response = await apiRequest<{ qrCode?: string; status: string }>(`/instances/${instanceId}/qr`)
      console.log('🔍 Resposta da API para QR Code:', response)
      return response.data
    } catch (error) {
      console.error('❌ Erro na requisição do QR Code:', error)
      setError(error instanceof Error ? error.message : 'Erro ao gerar QR Code')
      throw error
    }
  }

  const getInstanceStatus = async (instanceId: string) => {
    try {
      const response = await apiRequest<{ status: string; phone?: string }>(`/instances/${instanceId}/status`)
      
      // Atualizar instância na lista
      setInstances(prev => prev.map(inst => 
        inst.id === instanceId 
          ? { ...inst, status: response.data!.status as WhatsAppInstance['status'], phone: response.data!.phone }
          : inst
      ))
      
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao verificar status')
      throw error
    }
  }

  const deleteInstance = async (instanceId: string) => {
    setLoading(true)
    setError(null)
    try {
      await apiRequest(`/instances/${instanceId}`, 'DELETE')
      setInstances(prev => prev.filter(inst => inst.id !== instanceId))
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao deletar instância')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Templates de Mensagens
  const fetchTemplates = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<MessageTemplate[]>('/templates')
      setTemplates(response.data || [])
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao buscar templates')
    } finally {
      setLoading(false)
    }
  }

  const createTemplate = async (data: { name: string; type: string; content: string; isActive: boolean }) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<MessageTemplate>('/templates', 'POST', data)
      setTemplates(prev => [response.data!, ...prev])
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao criar template')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const updateTemplate = async (templateId: string, data: Partial<MessageTemplate>) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<MessageTemplate>(`/templates/${templateId}`, 'PUT', data)
      setTemplates(prev => prev.map(template => 
        template.id === templateId ? response.data! : template
      ))
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao atualizar template')
      throw error
    } finally {
      setLoading(false)
    }
  }

  const deleteTemplate = async (templateId: string) => {
    setLoading(true)
    setError(null)
    try {
      await apiRequest(`/templates/${templateId}`, 'DELETE')
      setTemplates(prev => prev.filter(template => template.id !== templateId))
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao deletar template')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Configurações
  const fetchSettings = async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<MessagingSettings>('/settings')
      setSettings(response.data || null)
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao buscar configurações')
    } finally {
      setLoading(false)
    }
  }

  const updateSettings = async (data: MessagingSettings) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest<MessagingSettings>('/settings', 'PUT', data)
      setSettings(response.data || null)
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao atualizar configurações')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Logs de Mensagens
  const fetchLogs = async (params?: { page?: number; limit?: number; status?: string; instanceId?: string }) => {
    setLoading(true)
    setError(null)
    try {
      const queryParams = new URLSearchParams()
      if (params?.page) queryParams.append('page', params.page.toString())
      if (params?.limit) queryParams.append('limit', params.limit.toString())
      if (params?.status) queryParams.append('status', params.status)
      if (params?.instanceId) queryParams.append('instanceId', params.instanceId)

      const response = await apiRequest<{ logs: MessageLog[]; pagination: any }>(`/logs?${queryParams}`)
      setLogs(response.data?.logs || [])
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao buscar logs')
    } finally {
      setLoading(false)
    }
  }

  // Envio de Mensagem de Teste
  const sendTestMessage = async (data: { instanceId: string; templateId: string; phone: string; variables?: Record<string, string> }) => {
    setLoading(true)
    setError(null)
    try {
      const response = await apiRequest('/send-test', 'POST', data)
      return response.data
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Erro ao enviar mensagem de teste')
      throw error
    } finally {
      setLoading(false)
    }
  }

  // Função para extrair variáveis de um template
  const extractVariables = (content: string): string[] => {
    const matches = content.match(/\{\{(\w+)\}\}/g)
    return matches ? matches.map(match => match.replace(/\{\{|\}\}/g, '')) : []
  }

  // Função para substituir variáveis em um template
  const replaceVariables = (content: string, variables: Record<string, string>): string => {
    let result = content
    Object.entries(variables).forEach(([key, value]) => {
      result = result.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
    })
    return result
  }

  // Função para verificar se uma instância está conectada
  const isInstanceConnected = (instance: WhatsAppInstance): boolean => {
    return instance.status === 'connected'
  }

  // Função para obter o status formatado
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'connected': return 'Conectado'
      case 'connecting': return 'Conectando...'
      case 'disconnected': return 'Desconectado'
      case 'error': return 'Erro'
      default: return 'Desconhecido'
    }
  }

  // Função para obter a cor do status
  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'connected': return 'text-green-600 bg-green-100'
      case 'connecting': return 'text-yellow-600 bg-yellow-100'
      case 'disconnected': return 'text-gray-600 bg-gray-100'
      case 'error': return 'text-red-600 bg-red-100'
      default: return 'text-gray-600 bg-gray-100'
    }
  }

  // Função para obter o tipo formatado
  const getTypeLabel = (type: string): string => {
    switch (type) {
      case 'welcome': return 'Boas-vindas'
      case 'reminder': return 'Lembrete'
      case 'motivation': return 'Motivação'
      case 'goal': return 'Meta'
      case 'custom': return 'Personalizado'
      default: return type
    }
  }

  // Carregar dados iniciais
  useEffect(() => {
    fetchInstances()
    fetchTemplates()
    fetchSettings()
  }, [])

  return {
    // Estado
    instances,
    templates,
    settings,
    logs,
    campaigns,
    loading,
    error,

    // Funções de instâncias
    fetchInstances,
    createInstance,
    getInstanceQR,
    getInstanceStatus,
    deleteInstance,

    // Funções de templates
    fetchTemplates,
    createTemplate,
    updateTemplate,
    deleteTemplate,

    // Funções de configurações
    fetchSettings,
    updateSettings,

    // Funções de logs
    fetchLogs,

    // Funções de envio
    sendTestMessage,

    // Funções utilitárias
    extractVariables,
    replaceVariables,
    isInstanceConnected,
    getStatusText,
    getStatusColor,
    getTypeLabel,

    // Funções de limpeza
    clearError: () => setError(null)
  }
} 